'use client'

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { WINS_PER_PAGE } from '@/constants/winsPerPage'
import { useDebounce } from '@/hooks/useDebounce'

import type { Tab } from '@/components/TabSelect'
import type { Topic, User, Win } from '@/payload-types'

export const winsScopeTabs: Tab[] = [
  { name: 'All wins', value: 'all' },
  { name: 'National', value: 'national' },
  { name: 'State', value: 'state' },
]

interface WinsContextType {
  // Data
  wins: Win[]
  winTopics: Topic[]
  winTopicCounts: { [key: number]: number }
  isFetchingTopicCounts: boolean

  // Current page data (paginated at source)
  filteredWins: Win[]
  totalDocs: number
  page: number
  setPage: (page: number) => void
  isListLoading: boolean

  // User
  payloadUser: User | null

  // Scope/Tab
  selectedScopeTab: (typeof winsScopeTabs)[number]['value']
  setSelectedScopeTab: (tab: (typeof winsScopeTabs)[number]['value']) => void

  // Topics filter
  selectedTopics: Topic[]
  setSelectedTopics: (topics: Topic[]) => void
  toggleTopic: (topic: Topic) => void
  clearTopics: () => void
  filterOpen: boolean
  setFilterOpen: (filterOpen: boolean) => void

  // Search
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  isSearching: boolean

  // Sorting
  sortDirection: 'asc' | 'desc'
  setSortDirection: (direction: 'asc' | 'desc') => void
  toggleSortDirection: () => void

  // Form modal
  isFormOpen: boolean
  setIsFormOpen: (open: boolean) => void
}

const WinsContext = createContext<WinsContextType | undefined>(undefined)

export const useWinsContext = () => {
  const context = useContext(WinsContext)
  if (!context) {
    throw new Error('useWinsContext must be used within a WinsProvider')
  }
  return context
}

type WinsProviderProps = {
  children: React.ReactNode
  initialWins: Win[]
  initialTotalDocs: number
  payloadUser: User | null
  winTopics: Topic[]
  initialWinTopicCounts: { [key: number]: number }
}

export const WinsProvider = ({
  children,
  initialWins,
  initialTotalDocs,
  payloadUser,
  winTopics,
  initialWinTopicCounts,
}: WinsProviderProps) => {
  // Scope tab
  const [selectedScopeTab, setSelectedScopeTab] =
    useState<(typeof winsScopeTabs)[number]['value']>('all')

  // Topics filter
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([])

  // Topic counts
  const [winTopicCounts, setWinTopicCounts] = useState<{ [key: number]: number }>(
    initialWinTopicCounts,
  )
  const [isFetchingTopicCounts, setIsFetchingTopicCounts] = useState<boolean>(false)
  const topicCountAbortRef = useRef<AbortController | null>(null)

  // Pagination (lifted so we can fetch by page)
  const [page, setPage] = useState(1)

  // Search
  const [searchTerm, setSearchTerm] = useState('')
  const [searchResults, setSearchResults] = useState<Win[] | null>(null)
  const [searchTotalDocs, setSearchTotalDocs] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  // List (non-search) API state - when filters/page require API
  const [listWins, setListWins] = useState<Win[]>([])
  const [listTotalDocs, setListTotalDocs] = useState(0)
  const [listLoading, setListLoading] = useState(false)
  const listAbortRef = useRef<AbortController | null>(null)

  // Sorting
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Form modal
  const [isFormOpen, setIsFormOpen] = useState(false)

  // Filter
  const [filterOpen, setFilterOpen] = useState(false)

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const isInitialMode =
    !debouncedSearchTerm.trim() &&
    page === 1 &&
    selectedScopeTab === 'all' &&
    selectedTopics.length === 0 &&
    sortDirection === 'desc'

  // Clear search state as soon as the debounced term empties
  const [prevDebouncedSearchTerm, setPrevDebouncedSearchTerm] = useState(debouncedSearchTerm)
  if (debouncedSearchTerm !== prevDebouncedSearchTerm) {
    setPrevDebouncedSearchTerm(debouncedSearchTerm)
    if (!debouncedSearchTerm.trim()) {
      if (searchResults) setSearchResults(null)
      if (searchTotalDocs) setSearchTotalDocs(0)
      if (isSearching) setIsSearching(false)
    }
  }

  // Fetch search results from API (paginated)
  useEffect(() => {
    if (!debouncedSearchTerm.trim()) return

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    const fetchSearchResults = async () => {
      setIsSearching(true)

      try {
        const params = new URLSearchParams()
        params.set('search', debouncedSearchTerm)
        params.set('limit', String(WINS_PER_PAGE))
        params.set('page', String(page))

        if (selectedScopeTab !== 'all') {
          params.set('scope', selectedScopeTab)
        }
        selectedTopics.forEach((topic) => {
          params.append('topic', String(topic.id))
        })
        params.set('sort', `${sortDirection === 'desc' ? '-winDate' : 'winDate'}`)

        const response = await fetch(`/api/frontend/wins?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to fetch search results')
        }

        const data = await response.json()
        setSearchResults(data.wins)
        setSearchTotalDocs(data.totalDocs ?? 0)
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Search error:', error)
          setSearchResults(null)
          setSearchTotalDocs(0)
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false)
        }
      }
    }

    fetchSearchResults()

    return () => {
      controller.abort()
    }
  }, [debouncedSearchTerm, page, selectedScopeTab, selectedTopics, sortDirection])

  // Toggle topic selection
  const toggleTopic = useCallback((topic: Topic) => {
    setSelectedTopics((prev) => {
      const exists = prev.some((t) => t.id === topic.id)
      if (exists) {
        return prev.filter((t) => t.id !== topic.id)
      }
      return [...prev, topic]
    })
  }, [])

  const clearTopics = useCallback(() => {
    setSelectedTopics([])
  }, [])

  const toggleSortDirection = useCallback(() => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
  }, [])

  // Reset to page 1 when any filter changes (context owns this; grid just uses
  // page/setPage). selectedTopics is compared by reference: it is state that
  // gets a new array identity on every update.
  const [prevFilters, setPrevFilters] = useState({
    searchTerm,
    selectedScopeTab,
    selectedTopics,
    sortDirection,
  })

  const hasFiltersChanged =
    searchTerm !== prevFilters.searchTerm ||
    selectedScopeTab !== prevFilters.selectedScopeTab ||
    selectedTopics !== prevFilters.selectedTopics ||
    sortDirection !== prevFilters.sortDirection

  if (hasFiltersChanged) {
    setPrevFilters({ searchTerm, selectedScopeTab, selectedTopics, sortDirection })
    if (page !== 1) setPage(1)
  }

  // When returning to initial mode (which implies an empty search term), re-sync
  // list state with the server-provided payload so that leaving initial mode
  // later doesn't briefly show stale results from a previous filter combination.
  const [prevIsInitialMode, setPrevIsInitialMode] = useState(isInitialMode)
  if (isInitialMode !== prevIsInitialMode) {
    setPrevIsInitialMode(isInitialMode)
    if (isInitialMode) {
      if (listWins !== initialWins) setListWins(initialWins)
      if (listTotalDocs !== initialTotalDocs) setListTotalDocs(initialTotalDocs)
    }
  }

  // Fetch list (non-search) when filters or page require API
  useEffect(() => {
    if (debouncedSearchTerm.trim() || isInitialMode) return

    if (listAbortRef.current) {
      listAbortRef.current.abort()
    }

    const controller = new AbortController()
    listAbortRef.current = controller

    const fetchList = async () => {
      setListLoading(true)

      try {
        const params = new URLSearchParams()
        params.set('limit', String(WINS_PER_PAGE))
        params.set('page', String(page))
        if (selectedScopeTab !== 'all') {
          params.set('scope', selectedScopeTab)
        }
        selectedTopics.forEach((topic) => {
          params.append('topic', String(topic.id))
        })
        params.set('sort', `${sortDirection === 'desc' ? '-winDate' : 'winDate'}`)

        const response = await fetch(`/api/frontend/wins?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to fetch wins')
        }

        const data = await response.json()
        setListWins(data.wins ?? [])
        setListTotalDocs(data.totalDocs ?? 0)
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('List fetch error:', error)
          setListWins([])
          setListTotalDocs(0)
        }
      } finally {
        if (!controller.signal.aborted) {
          setListLoading(false)
        }
      }
    }

    fetchList()

    return () => {
      controller.abort()
    }
  }, [debouncedSearchTerm, isInitialMode, page, selectedScopeTab, selectedTopics, sortDirection])

  const isDefaultTopicCounts = selectedScopeTab === 'all' && !debouncedSearchTerm.trim()

  // Back on the default scope with no search term: show the server-provided
  // counts again and drop any in-flight fetch state (the effect cleanup below
  // aborts the request itself).
  const [prevIsDefaultTopicCounts, setPrevIsDefaultTopicCounts] = useState(isDefaultTopicCounts)
  if (isDefaultTopicCounts !== prevIsDefaultTopicCounts) {
    setPrevIsDefaultTopicCounts(isDefaultTopicCounts)
    if (isDefaultTopicCounts) {
      if (winTopicCounts !== initialWinTopicCounts) setWinTopicCounts(initialWinTopicCounts)
      if (isFetchingTopicCounts) setIsFetchingTopicCounts(false)
    }
  }

  // Fetch scoped/searched topic counts from the API
  useEffect(() => {
    if (isDefaultTopicCounts) return

    if (topicCountAbortRef.current) {
      topicCountAbortRef.current.abort()
    }

    const controller = new AbortController()
    topicCountAbortRef.current = controller

    const fetchTopicCounts = async () => {
      setIsFetchingTopicCounts(true)

      try {
        const params = new URLSearchParams()

        if (debouncedSearchTerm.trim()) {
          params.set('search', debouncedSearchTerm)
        }

        if (selectedScopeTab !== 'all') {
          params.set('scope', selectedScopeTab)
        }

        const response = await fetch(`/api/frontend/wins/topic-counts?${params.toString()}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error('Failed to fetch topic counts')
        }

        const data = await response.json()
        setWinTopicCounts(data.counts ?? {})
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Topic counts fetch error:', error)
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsFetchingTopicCounts(false)
        }
      }
    }
    fetchTopicCounts()

    return () => controller.abort()
  }, [isDefaultTopicCounts, debouncedSearchTerm, selectedScopeTab])

  // Current page wins and total for the grid (paginated at source)
  const filteredWins =
    searchResults !== null ? searchResults : isInitialMode ? initialWins : listWins

  const totalDocs =
    searchResults !== null ? searchTotalDocs : isInitialMode ? initialTotalDocs : listTotalDocs

  const isListLoading = listLoading && !debouncedSearchTerm.trim() && !isInitialMode

  return (
    <WinsContext.Provider
      value={{
        payloadUser,
        wins: initialWins,
        winTopics,
        winTopicCounts,
        isFetchingTopicCounts,
        filteredWins,
        totalDocs,
        page,
        setPage,
        isListLoading,
        selectedScopeTab,
        setSelectedScopeTab,
        selectedTopics,
        setSelectedTopics,
        toggleTopic,
        clearTopics,
        filterOpen,
        setFilterOpen,
        searchTerm,
        setSearchTerm,
        isSearching,
        sortDirection,
        setSortDirection,
        toggleSortDirection,
        isFormOpen,
        setIsFormOpen,
      }}
    >
      {children}
    </WinsContext.Provider>
  )
}
